import React, { useCallback, useMemo, useState } from "react";
import "./../../styles/homePage/patientsList.scss";
import TablePage from "../../components/patientsList/genericTable/TablePage";
import { useTranslation } from "react-i18next";
import { TableCard } from "../../common/types/tableCardType";
import { useQuery } from "@tanstack/react-query";
import { exportCenters, getCenterCardsData } from "../../common/api/apiCalls";
import { svgs } from "../../common/assats";
import { useNavigate } from "react-router-dom";
import { debounce } from "@mui/material";
import { GenericRecord } from "../../common/types/filterObjType";
import { organizeSiteData } from "../../common/function/organizeSiteData";
import { fetchAllSites, getAllSites } from "../../common/api/siteService";
import Modal from "../../components/Modal/Modal";
import GenericList from "../../components/GenericList/GenericList";
import Loader from "../../components/Loader/Loader";

const sortKeyArray = [
  "siteName",
  "centerStatus",
  "relatedHCP",
  "relatedPatients",
  "contactPerson",
  "email",
  "phoneNumber",
  "centerAddress",
];

const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [sortKey, setSortKey] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [filters, setFilters] = useState<string[]>([]);
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<any[]>([]);
  const [selectedSite, setSelectedSite] = useState<{
    siteName: string;
    type: string;
  } | null>(null);

  const getText = (text: string) => t(`sitesManagement.${text}`);

  const {
    data: sitesData,
    error: fetchSitesError,
    isLoading: isLoadingSites,
  } = useQuery({
    queryKey: ["sitesData", sortKey, sortOrder, debouncedSearch, filters],
    queryFn: () => fetchAllSites(sortKey, sortOrder, debouncedSearch, filters),
  });

  // Second query, dependent on the first one
  const {
    data: filtersData,
    error: sitesDataError,
    isLoading: isLoadingFilters,
  } = useQuery({
    queryKey: ["centerData"],
    queryFn: getAllSites,
  });

  const {
    data: cardsData,
    error: cardsDataError,
    isLoading: isLoadingCards,
  } = useQuery({
    queryKey: ["cards"],
    queryFn: getCenterCardsData,
  });

  // Debounce the search input
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300), // 300ms delay
    []
  );

  // Update the search value when typing
  const handleSearchChange = (str: string) => {
    setSearch(str); // Instant feedback for the user
    debouncedSetSearch(str); // Delayed API request
  };

  // Handle clicking the relatedHCPs or relatedPatients button to open modal
  const handleListClick = (site: any, type: "HCPs" | "Patients") => {
    setSelectedList(type === "HCPs" ? site.relatedHCPs : site.relatedPatients);
    setSelectedSite({ siteName: site.SiteName, type });
    setIsModalOpen(true);
  };

  const cards: TableCard[] = useMemo(
    () => [
      {
        headline: getText("cards.devices"),
        icon: svgs.cards.DevicesIcon,
        value: isLoadingCards ? 0 : cardsData[0] || 0,
        action: () => {
          console.log("click");
        },
      },
      {
        headline: getText("cards.centers"),
        icon: svgs.cards.Centers,
        value: isLoadingCards ? 0 : cardsData[1] || 0,
        action: () => {
          console.log("click");
        },
      },
      {
        headline: getText("cards.Active_HCP"),
        icon: svgs.cards.PatientsVerified,
        value: isLoadingCards ? 0 : cardsData[2] || 0,
        action: () => {
          console.log("click");
        },
      },
    ],
    [cardsData]
  );

  function getFilters(data: { id: string; SiteName: string }[]) {
    if (!data || data.length === 0 || !Array.isArray(data)) {
      return {};
    }
    let filterObj: GenericRecord<string> = {
      center: [
        { name: "All", value: "" },
        ...data.map((val) => ({ name: val.SiteName, value: val.SiteName })),
      ],
      status: [
        { name: "All", value: "" },
        { name: "Active", value: "1" },
        { name: "Inactive", value: "0" },
      ],
    };
    return filterObj;
  }

  function handleRowClick(index: number) {
    console.log("index:", sitesData[index]);
    navigate("EditSite", {
      state: { user: sitesData[index] },
    });
  }
  if (isLoadingFilters || isLoadingCards) {
    return <Loader text={t("sitesManagement.loading")} />;
  }

  const handleExport = async () => {
    try {
      const blob = await exportCenters({
        sortKey,
        sortOrder,
        search: debouncedSearch,
        filters,
      });
      // Generate a downloadable URL and trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Centers_List.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url); // Cleanup URL
    } catch (error) {
      console.error("Error exporting devices:", error);
      alert("Failed to export devices. Please try again.");
    }
  };

  return (
    <>
      <TablePage
        subButtonText={getText("export")}
        subAction={handleExport}
        filterObj={filtersData ? getFilters(filtersData) : {}}
        tableHeadline={getText(`Sites_Management`)}
        headerHeadline={getText("Sites_Management")}
        cards={cards}
        trashAction={() => {}}
        filters={filters}
        setFilters={setFilters}
        searchValue={search}
        setSearchValue={handleSearchChange}
        searchPlaceholder={getText(
          "filter.search_site_by_site_name_related_hcp_status"
        )}
        buttonText={getText("Create_New")}
        buttonAction={() => {
          navigate("CreateSite");
        }}
        setSortKey={setSortKey}
        sortKey={sortKey}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        tableData={
          isLoadingSites
            ? []
            : organizeSiteData(sitesData).map((site) => ({
                ...site,
                relatedHCPs: (
                  <button
                    className="link-button"
                    onClick={() => {
                      if (site.relatedHCPs?.length > 0) {
                        handleListClick(site, "HCPs");
                      }
                    }}
                  >
                    {site.relatedHCPs?.length || 0}
                  </button>
                ),
                relatedPatients: (
                  <button
                    className="link-button"
                    onClick={() => {
                      if (site.relatedPatients?.length > 0) {
                        handleListClick(site, "Patients");
                      }
                    }}
                  >
                    {site.relatedPatients?.length || 0}
                  </button>
                ),
              }))
        }
        sortKeysArray={sortKeyArray.map((value) => {
          return { name: getText(`sort.${value}`), val: value };
        })}
        rowClick={handleRowClick}
        EndRowIcon={svgs.icons.pincel}
      />
      {/* Modal for showing related HCPs or related Patients */}
      <Modal
        isOpen={isModalOpen}
        title={`Related ${selectedSite?.type}`}
        description={selectedSite ? `Site: ${selectedSite.siteName}` : ""}
        content={
          <GenericList
            items={selectedList}
            filterKey="firstName"
            renderItem={(person) => (
              <div className="list-item">
                {person.firstName} {person.lastName}
              </div>
            )}
            placeholder={`Search ${selectedSite?.type.toLowerCase()}...`}
          />
        }
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default Users;
